/* ============================================================
   PRODUCT STICKY NAV — product-sticky-nav.js
 
   Features:
   ─ Smooth scroll to sections (offset-corrected for sticky nav)
   ─ Scroll spy — active nav item updates as user scrolls
   ─ Mobile dropdown — open/close, close on outside click
   ─ Mobile current label updates when section changes
   ─ Collapsible sections — toggle + auto-expand on nav click
   ─ ESC key closes mobile dropdown
   ============================================================ */
 
(function () {
  "use strict";
 
  /* ── Config ──────────────────────────────────────────── */
  const SCROLL_OFFSET_EXTRA = 16; // extra px so heading isn't hidden
 
  /* ── Elements ────────────────────────────────────────── */
  const wrap       = document.getElementById("psnWrap");
  const trigger    = document.getElementById("psnTrigger");
  const dropdown   = document.getElementById("psnDropdown");
  const currentLbl = document.getElementById("psnCurrent");
 
  if (!wrap) return;
 
  /* ── Calculate total scroll offset ───────────────────── */
  function getNavOffset() {
    const stickyTop  = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--psn-top") || "60");
    const navHeight  = wrap.offsetHeight || 52;
    return stickyTop + navHeight + SCROLL_OFFSET_EXTRA;
  }
 
  /* Set CSS var so ::before pseudo-elements use the right value */
  function syncCssOffset() {
    const stickyTop = parseInt(
      getComputedStyle(wrap).top || "60"
    ) || 60;
    document.documentElement.style.setProperty("--psn-top", stickyTop + "px");
  }
  syncCssOffset();
 
  /* ── SMOOTH SCROLL ────────────────────────────────────── */
  function scrollToSection(targetId) {
    const target = document.getElementById(targetId);
    if (!target) return;
 
    /* If section is collapsed, expand it first */
    const body = document.getElementById("body-" + targetId);
    if (body && body.classList.contains("psn-collapsed")) {
      expandSection(targetId);
    }
 
    /* Small delay so expand animation starts before scroll */
    setTimeout(function () {
      const offset = getNavOffset();
      const top    = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }, body && body.classList.contains("psn-collapsed") ? 60 : 0);
  }
 
  /* ── DESKTOP NAV LINKS ────────────────────────────────── */
  document.querySelectorAll(".psn-link").forEach(function (link) {
    link.addEventListener("click", function (e) {
      e.preventDefault();
      const target = this.dataset.target;
      if (target) scrollToSection(target);
    });
  });
 
  /* ── MOBILE DROPDOWN LINKS ────────────────────────────── */
  document.querySelectorAll(".psn-mobile-item").forEach(function (item) {
    item.addEventListener("click", function (e) {
      e.preventDefault();
      const target = this.dataset.target;
      closeMobileDropdown();
 
      if (currentLbl) {
        currentLbl.textContent = this.textContent.trim();
      }
 
      /* Mark active */
      document.querySelectorAll(".psn-mobile-item").forEach(function (i) {
        i.classList.remove("psn-mobile-item--active");
        i.setAttribute("aria-selected", "false");
      });
      this.classList.add("psn-mobile-item--active");
      this.setAttribute("aria-selected", "true");
 
      if (target) scrollToSection(target);
    });
  });
 
  /* ── MOBILE DROPDOWN TOGGLE ───────────────────────────── */
  function openMobileDropdown() {
    dropdown.removeAttribute("hidden");
    trigger.setAttribute("aria-expanded", "true");
  }
 
  function closeMobileDropdown() {
    dropdown.setAttribute("hidden", "");
    trigger.setAttribute("aria-expanded", "false");
  }
 
  if (trigger && dropdown) {
    trigger.addEventListener("click", function () {
      const isOpen = trigger.getAttribute("aria-expanded") === "true";
      isOpen ? closeMobileDropdown() : openMobileDropdown();
    });
 
    /* Close on outside click */
    document.addEventListener("click", function (e) {
      if (!trigger.contains(e.target) && !dropdown.contains(e.target)) {
        closeMobileDropdown();
      }
    });
 
    /* Close on ESC */
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeMobileDropdown();
    });
  }
 
  /* ── COLLAPSIBLE SECTIONS ─────────────────────────────── */
  function expandSection(sectionId) {
    const body   = document.getElementById("body-" + sectionId);
    const header = document.querySelector("[data-toggle='" + sectionId + "']");
    const btn    = header ? header.querySelector(".psn-section-toggle") : null;
 
    if (!body) return;
 
    /* Measure natural height */
    body.classList.remove("psn-collapsed");
    body.style.height = "auto";
    const h = body.scrollHeight;
    body.style.height = "0px";
 
    /* Force reflow then animate */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        body.style.height = h + "px";
        /* After transition, set to auto so content resize works */
        body.addEventListener("transitionend", function onEnd() {
          body.style.height = "auto";
          body.removeEventListener("transitionend", onEnd);
        });
      });
    });
 
    if (btn) btn.setAttribute("aria-expanded", "true");
  }
 
  function collapseSection(sectionId) {
    const body   = document.getElementById("body-" + sectionId);
    const header = document.querySelector("[data-toggle='" + sectionId + "']");
    const btn    = header ? header.querySelector(".psn-section-toggle") : null;
 
    if (!body) return;
 
    /* Lock to current pixel height, then animate to 0 */
    body.style.height = body.scrollHeight + "px";
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        body.classList.add("psn-collapsed");
        body.style.height = "0px";
      });
    });
 
    if (btn) btn.setAttribute("aria-expanded", "false");
  }
 
  /* Attach toggle to each section header */
  document.querySelectorAll(".psn-section-header").forEach(function (header) {
    header.addEventListener("click", function () {
      const sectionId = this.dataset.toggle;
      if (!sectionId) return;
 
      const body = document.getElementById("body-" + sectionId);
      if (!body) return;
 
      if (body.classList.contains("psn-collapsed")) {
        expandSection(sectionId);
      } else {
        collapseSection(sectionId);
      }
    });
  });
 
  /* ── SCROLL SPY ───────────────────────────────────────── */
  const sectionIds = [
    "section-oren-owners",
    "section-video-testimonials",
    "section-whats-in-box",
    "section-overview",
    "section-features",
    "section-tech-specs",
    "section-faq"
  ];
 
  const mobileSectionIds = [
    "section-features",
    "section-tech-specs",
    "section-faq"
  ];
 
  function getActiveSectionId() {
    const offset = getNavOffset() + 40; // look 40px below nav bottom
    let active   = null;
 
    sectionIds.forEach(function (id) {
      const el = document.getElementById(id);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top <= offset) active = id;
    });
 
    return active;
  }
 
  function updateDesktopActive(activeId) {
    document.querySelectorAll(".psn-link").forEach(function (link) {
      const isActive = link.dataset.target === activeId;
      link.classList.toggle("psn-active", isActive);
    });
  }
 
  function updateMobileActive(activeId) {
    if (!currentLbl) return;
 
    /* Find matching mobile item */
    let found = false;
    document.querySelectorAll(".psn-mobile-item").forEach(function (item) {
      const isActive = item.dataset.target === activeId;
      item.classList.toggle("psn-mobile-item--active", isActive);
      item.setAttribute("aria-selected", isActive ? "true" : "false");
      if (isActive) {
        currentLbl.textContent = item.textContent.trim();
        found = true;
      }
    });
 
    /* If the active section isn't in mobile nav, don't change label */
  }
 
  function updateNavShadow() {
    const scrolled = window.scrollY > 10;
    wrap.classList.toggle("psn-scrolled", scrolled);
  }
 
  let ticking = false;
  window.addEventListener("scroll", function () {
    if (!ticking) {
      requestAnimationFrame(function () {
        const activeId = getActiveSectionId();
        updateDesktopActive(activeId);
        updateMobileActive(activeId);
        updateNavShadow();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });
 
  /* Initial state */
  updateDesktopActive(getActiveSectionId());
  updateNavShadow();
 
})();